/**
 * Dashboard API client.
 *
 * Deliberately not the @mailgiri/node SDK: that authenticates with a bearer API
 * key, and the dashboard authenticates with a session cookie. Two different
 * credentials with two different rules -- the cookie needs `credentials` on every
 * request and the CSRF header on every write, and pretending otherwise would mean
 * bending the SDK into something it should not be.
 *
 * Same-origin by design. In development Vite proxies /v1 to the API; in Docker
 * nginx fronts both. Either way the cookie is first-party, so SameSite=Strict
 * works and there is no CORS in the request path.
 */

/** One turn of assistant conversation.
 *
 *  `messages` is the transcript to post back next turn. The server keeps nothing:
 *  a conversation about somebody's mail log leaves no copy behind, which is the
 *  right default for a platform that also promises erasure.
 */
export type AssistantTurn = {
  reply: string;
  messages: Record<string, unknown>[];
  tools_used: string[];
  /** The loop hit its tool budget without reaching an answer. Say so rather than
   *  presenting a non-answer as an answer. */
  truncated: boolean;
  proposals: AssistantProposal[];
};

/** A write the assistant drew up and has *not* performed.
 *
 *  The click on the confirmation is what performs it. That is the containment for
 *  prompt injection, not a nicety — read tools return text strangers wrote, and
 *  no instruction hidden in that text can produce an action.
 */
export type AssistantProposal = {
  action: string;
  arguments: Record<string, unknown>;
  /** Written for the person clicking: what happens in the world. */
  summary: string;
  effect: string;
};

export type AssistantStatus = {
  available: boolean;
  model: string | null;
  endpoint: string | null;
  /** A guess, and labelled as one wherever it is shown. False means this
   *  deployment sends message subjects and recipient addresses to a third party. */
  self_hosted: boolean | null;
  requires_api_key: boolean | null;
  how_to_configure?: string;
  max_tool_calls?: number;
  /** Scoped to the signed-in person's role, so the panel never offers something
   *  their role forbids. */
  tools: { name: string; description: string; writes: boolean }[];
};

/** Matches the API's error envelope. Branch on `code`, never on the message. */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: Record<string, unknown>;

  constructor(message: string, code: string, status: number, details: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

const SAFE_METHODS = new Set(['GET', 'HEAD']);

async function request<T>(
  method: string,
  path: string,
  options: { body?: unknown; query?: Record<string, string | number | undefined> } = {},
): Promise<T> {
  const url = new URL(path, window.location.origin);
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== '') url.searchParams.set(key, String(value));
  }

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  // The server refuses cookie-authenticated writes without this. A cross-origin
  // form post cannot set a custom header, which is the point.
  if (!SAFE_METHODS.has(method)) headers['X-Mailgiri-Dashboard'] = '1';

  const response = await fetch(url, {
    method,
    headers,
    // Sends the HttpOnly session cookie. Without it every call is anonymous.
    credentials: 'same-origin',
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  if (response.status === 204) return null as T;

  const text = await response.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!response.ok) {
    let code = 'unknown_error';
    let message = `Request failed (${response.status})`;
    let details: Record<string, unknown> = {};

    if (parsed && typeof parsed === 'object' && 'error' in parsed) {
      const error = (parsed as { error: Record<string, unknown> }).error;
      if (typeof error?.['code'] === 'string') code = error['code'];
      if (typeof error?.['message'] === 'string') message = error['message'];
      details = error ?? {};
      // Validation errors carry a per-field list; surface the first one, since a
      // form can only usefully point at one thing at a time.
      const fieldErrors = error?.['details'];
      if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
        const first = fieldErrors[0] as { field?: string; issue?: string };
        message = first.field ? `${first.field}: ${first.issue}` : message;
      }
    } else if (typeof parsed === 'string' && parsed.trim()) {
      message = parsed.slice(0, 300);
    }

    throw new ApiError(message, code, response.status, details);
  }

  return parsed as T;
}

// --- types, mirroring the API responses ---

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  plan: string;
  mail_type: string;
  reputation_tier: string;
  status: string;
  sending_domain: string;
}

/** Present only when an operator is viewing this dashboard through the admin
 *  console (admin spec §9). Its presence drives a persistent banner: a session
 *  that looks identical to a real one is how somebody forgets whose account they
 *  are in. */
export type Impersonation = {
  admin_email: string;
  tenant_name: string;
  reason: string;
  expires_at: string;
  read_only: boolean;
};

export interface Me {
  user_id: string;
  email: string;
  org_role: string;
  tenant: Tenant;
  impersonation?: Impersonation | null;
}

export interface MessageEvent {
  id: string;
  event_type: string;
  sub_type: string | null;
  recipient: string | null;
  occurred_at: string;
}

export interface Message {
  id: string;
  status: string;
  from_address: string;
  to_address: string;
  subject: string | null;
  tags: { name: string; value: string }[] | null;
  environment: string;
  source: string;
  scheduled_at: string | null;
  sent_at: string | null;
  failure_reason: string | null;
  created_at: string;
}

export interface MessageDetail extends Message {
  events: MessageEvent[];
}

export interface DnsRecord {
  type: string;
  name: string;
  value: string;
  priority: number | null;
  purpose: string;
}

export interface Domain {
  id: string;
  domain_name: string;
  verification_status: string;
  mail_from_status: string;
  mail_from_subdomain: string | null;
  is_platform_subdomain: boolean;
  mail_type: string;
  created_at: string;
  records?: DnsRecord[];
}

export interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  environment: string;
  expires_at: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
  key?: string;
}

export interface Template {
  id: string;
  name: string;
  subject_template: string | null;
  html_template: string | null;
  text_template: string | null;
  created_at: string;
  updated_at: string;
}

export interface Suppression {
  id: string;
  email_address: string;
  reason: string;
  created_at: string;
}

export interface Webhook {
  id: string;
  target_url: string;
  subscribed_events: string[];
  is_active: boolean;
  created_at: string;
  signing_secret?: string;
}

export interface WebhookDelivery {
  id: string;
  webhook_id: string;
  event_id: string | null;
  event_type: string;
  attempt_count: number;
  last_status_code: number | null;
  last_error: string | null;
  delivered_at: string | null;
  next_retry_at: string | null;
  created_at: string;
}

export interface Analytics {
  summary: {
    window_days: number;
    sent: number;
    delivered: number;
    bounced: number;
    complained: number;
    failed: number;
    opened: number;
    clicked: number;
    suppressed_total: number;
    delivery_rate: number;
    bounce_rate: number;
    complaint_rate: number;
    open_rate: number;
    click_rate: number;
  };
  timeseries: {
    date: string;
    sent: number;
    delivered: number;
    bounced: number;
    complained: number;
    opened: number;
    clicked: number;
  }[];
  by_domain: { label: string; sent: number; delivered: number; bounced: number }[];
  by_tag: { label: string; sent: number; delivered: number; bounced: number }[];
}

export interface TeamMember {
  user_id: string;
  email: string;
  role: string;
  joined_at: string | null;
}

export interface TeamInvite {
  id: string;
  email: string;
  role: string;
  expires_at: string;
  created_at: string;
  invited_by: string | null;
}

export interface CreatedInvite extends TeamInvite {
  /** Returned once. Only a hash is stored, so this cannot be recovered. */
  invite_url: string;
}

export interface Team {
  members: TeamMember[];
  pending_invites: TeamInvite[];
  your_role: string;
}

export interface Audience {
  id: string;
  name: string;
  description: string | null;
  contact_count: number;
  subscribed_count: number;
  /** Of the subscribed, how many actually clicked a confirmation link. The gap
   *  between this and `subscribed_count` is the list a customer cannot account
   *  for — imported or predating double opt-in — and it is worth showing. */
  confirmed_count: number;
  created_at: string;
}

export interface Contact {
  id: string;
  audience_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  subscribed: boolean;
  unsubscribed_at: string | null;
  /** 'pending' | 'confirmed' | 'imported_asserted' | 'legacy_import' */
  consent_source: string;
  confirmed_at: string | null;
  confirmation_sent_at: string | null;
  custom_fields: Record<string, unknown> | null;
  created_at: string;
}

export interface ImportResult {
  created: number;
  updated: number;
  rejected: number;
  /** Every rejection with its line number — a bare count is unactionable. */
  errors: { line: number; reason: string; value: string }[];
}

export type BroadcastStatus =
  | 'draft'
  | 'scheduled'
  | 'sending'
  | 'sent'
  | 'canceled'
  | 'failed';

export interface Broadcast {
  id: string;
  name: string;
  audience_id: string;
  template_id: string | null;
  from_address: string;
  reply_to: string | null;
  subject: string | null;
  status: BroadcastStatus;
  environment: string;
  scheduled_at: string | null;
  started_at: string | null;
  sent_at: string | null;
  failure_reason: string | null;
  total_recipients: number;
  sent_count: number;
  skipped_count: number;
  failed_count: number;
  created_at: string;
}

export interface BroadcastDetail extends Broadcast {
  html: string | null;
  text: string | null;
  audience_subscribed: number;
  audience_total: number;
}

export interface BroadcastRecipient {
  id: string;
  contact_id: string;
  message_id: string | null;
  status: string;
  skip_reason: string | null;
  failure_reason: string | null;
}

export interface BroadcastPreview {
  sampled_contact: string | null;
  subject: string | null;
  html: string | null;
  text: string | null;
  /** Placeholders the audience cannot populate. Read this before sending. */
  unresolved_placeholders: string[];
}

export interface AuditEntry {
  id: string;
  action: string;
  target_type: string | null;
  target_id: string | null;
  actor: string;
  metadata: Record<string, unknown> | null;
  occurred_at: string;
}

// --- endpoints ---

export interface HealthSignal {
  name: string;
  severity: 'ok' | 'warning' | 'critical' | 'unknown';
  value: number;
  threshold: number;
  sample_size: number;
  summary: string;
  detail: Record<string, number | string>;
}

export interface HealthReport {
  status: 'ok' | 'warning' | 'critical';
  checked_at: string;
  signals: HealthSignal[];
}

export interface IpPool {
  id: string;
  name: string;
  ses_pool_name: string;
  mail_type: string;
  warm_up_status: string;
  warm_up_day: number;
  daily_cap: number;
  sent_today: number;
  ip_addresses: string[];
  domain_count: number;
  created_at: string;
}

export interface PoolEligibility {
  eligible: boolean;
  plan: string;
  recent_volume: number;
  required_volume: number;
  reasons: string[];
}

export interface InboundRoute {
  id: string;
  address_pattern: string;
  domain_name: string;
  target_webhook_url: string;
  is_active: boolean;
  forward_attachments: boolean;
  received_count: number;
  last_received_at: string | null;
  created_at: string;
}

export interface CreatedInboundRoute extends InboundRoute {
  signing_secret: string;
}

export interface SsoConfig {
  id: string;
  email_domain: string;
  issuer: string;
  client_id: string;
  provider: string;
  enforced: boolean;
  default_role: string;
  is_active: boolean;
  created_at: string;
}

export interface ErasureResult {
  email: string;
  contacts_deleted: number;
  messages_anonymised: number;
  events_anonymised: number;
  broadcast_rows_detached: number;
  suppression_retained: boolean;
  notes: string[];
}

export interface PlanSummary {
  key: string;
  label: string;
  price_cents: number | null;
  price_is_placeholder: boolean;
  included_volume: number;
  overage_per_thousand_cents: number | null;
  custom_domains: number | null;
  webhooks: number | null;
  team_seats: number | null;
  dedicated_ip: string;
  hard_volume_cap: boolean;
  self_serve: boolean;
  purchasable: boolean;
}

export interface BillingState {
  plan: PlanSummary;
  usage: {
    used: number;
    included: number;
    remaining: number;
    over_by: number;
    blocked: boolean;
    reason: string | null;
  };
  limits: { name: string; used: number; limit: number | null }[];
  subscription: {
    status: string | null;
    current_period_end: string | null;
    cancel_at_period_end: boolean;
    has_payment_method: boolean;
  };
  overage: {
    period_start: string;
    period_end: string;
    used: number;
    included: number;
    over_by: number;
    estimated_overage_cents: number;
  };
}

export interface UsageSeries {
  start: string;
  end: string;
  days: { date: string; environment: string; sent: number; broadcast: number }[];
  billable_total: number;
  test_total: number;
}

export const api = {
  auth: {
    me: () => request<Me>('GET', '/v1/auth/me'),
    login: (email: string, password: string) =>
      request<Me>('POST', '/v1/auth/login', { body: { email, password } }),
    /** Which sign-in methods this deployment offers. Asked before rendering
     *  "Continue with Google": whether the OAuth client is configured is a
     *  deploy-time fact the browser cannot otherwise know. */
    providers: () =>
      request<{ password: boolean; google: boolean }>('GET', '/v1/auth/providers'),

    signup: (email: string, password: string) =>
      request<{
        user_id: string;
        tenant: Tenant;
        // No keys. Signup does not mint any -- the user creates them on the API
        // keys page, where the value is shown once and can be copied. Two keys
        // handed over at signup could not be copied at all and were, in
        // practice, two secrets nobody had.
      }>('POST', '/v1/auth/signup', { body: { email, password } }),
    logout: () => request<null>('POST', '/v1/auth/logout'),
  },

  emails: {
    list: (
      query: { limit?: number; cursor?: string; status?: string; search?: string } = {},
    ) =>
      request<{ data: Message[]; next_cursor: string | null }>('GET', '/v1/emails', { query }),
    get: (id: string) => request<MessageDetail>('GET', `/v1/emails/${id}`),
    cancel: (id: string) => request<Message>('POST', `/v1/emails/${id}/cancel`),
    send: (body: Record<string, unknown>) =>
      request<{ id: string; status: string }>('POST', '/v1/emails', { body }),
  },

  domains: {
    list: () => request<Domain[]>('GET', '/v1/domains'),
    get: (id: string) => request<Domain>('GET', `/v1/domains/${id}`),
    create: (name: string, mail_type: string) =>
      request<Domain>('POST', '/v1/domains', { body: { name, mail_type } }),
    remove: (id: string) => request<null>('DELETE', `/v1/domains/${id}`),
  },

  apiKeys: {
    list: () => request<ApiKey[]>('GET', '/v1/api-keys'),
    create: (body: { name: string; environment: string; scopes: string[] }) =>
      request<ApiKey>('POST', '/v1/api-keys', { body }),
    revoke: (id: string) => request<null>('DELETE', `/v1/api-keys/${id}`),
  },

  templates: {
    list: () => request<Template[]>('GET', '/v1/templates'),
    create: (body: Record<string, unknown>) =>
      request<Template>('POST', '/v1/templates', { body }),
    update: (id: string, body: Record<string, unknown>) =>
      request<Template>('PUT', `/v1/templates/${id}`, { body }),
    remove: (id: string) => request<null>('DELETE', `/v1/templates/${id}`),
  },

  suppressions: {
    list: (limit = 200) => request<Suppression[]>('GET', '/v1/suppressions', { query: { limit } }),
    add: (email: string, reason: string) =>
      request<Suppression>('POST', '/v1/suppressions', { body: { email, reason } }),
    remove: (email: string) =>
      request<null>('DELETE', `/v1/suppressions/${encodeURIComponent(email)}`),
  },

  webhooks: {
    list: () => request<Webhook[]>('GET', '/v1/webhooks'),
    create: (body: { target_url: string; subscribed_events: string[] }) =>
      request<Webhook>('POST', '/v1/webhooks', { body }),
    update: (id: string, body: Record<string, unknown>) =>
      request<Webhook>('PATCH', `/v1/webhooks/${id}`, { body }),
    remove: (id: string) => request<null>('DELETE', `/v1/webhooks/${id}`),
    deliveries: (id: string) =>
      request<WebhookDelivery[]>('GET', `/v1/webhooks/${id}/deliveries`, { query: { limit: 50 } }),
    retry: (webhookId: string, deliveryId: string) =>
      request<WebhookDelivery>(
        'POST',
        `/v1/webhooks/${webhookId}/deliveries/${deliveryId}/retry`,
      ),
    sendTest: (id: string) => request<WebhookDelivery>('POST', `/v1/webhooks/${id}/test`),
  },

  analytics: (days = 30) => request<Analytics>('GET', '/v1/analytics', { query: { days } }),

  auditLogs: (limit = 100) => request<AuditEntry[]>('GET', '/v1/audit-logs', { query: { limit } }),

  audiences: {
    list: () => request<Audience[]>('GET', '/v1/audiences'),
    get: (id: string) => request<Audience>('GET', `/v1/audiences/${id}`),
    create: (name: string, description?: string) =>
      request<Audience>('POST', '/v1/audiences', { body: { name, description } }),
    remove: (id: string) => request<null>('DELETE', `/v1/audiences/${id}`),
    contacts: (id: string, query: { limit?: number; search?: string } = {}) =>
      request<Contact[]>('GET', `/v1/audiences/${id}/contacts`, { query }),
    addContacts: (id: string, contacts: Record<string, unknown>[]) =>
      request<ImportResult>('POST', `/v1/audiences/${id}/contacts`, {
        body: { contacts },
      }),
    /** Raw CSV upload. Sent as text/csv, not multipart. */
    importCsv: async (id: string, csv: string): Promise<ImportResult> => {
      const response = await fetch(`/v1/audiences/${id}/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'text/csv', 'X-Mailgiri-Dashboard': '1' },
        credentials: 'same-origin',
        body: csv,
      });
      const parsed = await response.json();
      if (!response.ok) {
        throw new ApiError(
          parsed?.error?.message ?? 'Import failed',
          parsed?.error?.code ?? 'unknown_error',
          response.status,
          parsed?.error ?? {},
        );
      }
      return parsed as ImportResult;
    },
  },

  contacts: {
    unsubscribe: (id: string) =>
      request<Contact>('POST', `/v1/contacts/${id}/unsubscribe`),
    resubscribe: (id: string) =>
      request<Contact>('POST', `/v1/contacts/${id}/resubscribe`),
    remove: (id: string) => request<null>('DELETE', `/v1/contacts/${id}`),
  },

  broadcasts: {
    list: () => request<Broadcast[]>('GET', '/v1/broadcasts'),
    get: (id: string) => request<BroadcastDetail>('GET', `/v1/broadcasts/${id}`),
    create: (body: Record<string, unknown>) =>
      request<Broadcast>('POST', '/v1/broadcasts', { body }),
    update: (id: string, body: Record<string, unknown>) =>
      request<Broadcast>('PATCH', `/v1/broadcasts/${id}`, { body }),
    preview: (id: string) =>
      request<BroadcastPreview>('GET', `/v1/broadcasts/${id}/preview`),
    send: (id: string, scheduledAt?: string) =>
      request<Broadcast>('POST', `/v1/broadcasts/${id}/send`, {
        body: { scheduled_at: scheduledAt ?? null },
      }),
    cancel: (id: string) => request<Broadcast>('POST', `/v1/broadcasts/${id}/cancel`),
    recipients: (id: string, status?: string) =>
      request<BroadcastRecipient[]>('GET', `/v1/broadcasts/${id}/recipients`, {
        query: { limit: 200, status },
      }),
  },

  team: {
    get: () => request<Team>('GET', '/v1/team'),
    invite: (email: string, role: string) =>
      request<CreatedInvite>('POST', '/v1/team/invites', { body: { email, role } }),
    revokeInvite: (inviteId: string) =>
      request<null>('DELETE', `/v1/team/invites/${inviteId}`),
    removeMember: (userId: string) =>
      request<null>('DELETE', `/v1/team/members/${userId}`),
    acceptInvite: (token: string, password?: string) =>
      request<{ user_id: string; email: string; role: string }>(
        'POST',
        '/v1/auth/accept-invite',
        { body: { token, password: password || null } },
      ),
  },
  health: {
    signals: () => request<HealthReport>('GET', '/v1/health-signals'),
  },

  ipPools: {
    list: () => request<IpPool[]>('GET', '/v1/ip-pools'),
    eligibility: () => request<PoolEligibility>('GET', '/v1/ip-pools/eligibility'),
    create: (name: string, mail_type: string) =>
      request<IpPool>('POST', '/v1/ip-pools', { body: { name, mail_type } }),
    assign: (domain_id: string, pool_id: string | null) =>
      request<{ domain_id: string; pool_id: string | null }>('POST', '/v1/ip-pools/assign', {
        body: { domain_id, pool_id },
      }),
    startWarmUp: (poolId: string) =>
      request<IpPool>('POST', `/v1/ip-pools/${poolId}/warm-up`),
    pause: (poolId: string) => request<IpPool>('POST', `/v1/ip-pools/${poolId}/pause`),
  },

  inbound: {
    list: () => request<InboundRoute[]>('GET', '/v1/inbound-routes'),
    create: (body: {
      address_pattern: string;
      domain_name: string;
      target_webhook_url: string;
      forward_attachments: boolean;
    }) => request<CreatedInboundRoute>('POST', '/v1/inbound-routes', { body }),
    remove: (id: string) => request<null>('DELETE', `/v1/inbound-routes/${id}`),
  },

  sso: {
    get: () => request<SsoConfig | null>('GET', '/v1/sso'),
    configure: (body: {
      email_domain: string;
      issuer: string;
      client_id: string;
      client_secret: string;
      default_role: string;
    }) => request<SsoConfig>('PUT', '/v1/sso', { body }),
    enforce: (enforced: boolean) =>
      request<SsoConfig>('POST', '/v1/sso/enforce', {
        query: { enforced: String(enforced) },
      }),
  },

  privacy: {
    preview: (email: string) =>
      request<Record<string, number>>('GET', '/v1/privacy/erasure-preview', {
        query: { email },
      }),
    exportSubject: (email: string) =>
      request<Record<string, unknown>>('GET', '/v1/privacy/export', { query: { email } }),
    erase: (email: string) =>
      request<ErasureResult>('POST', '/v1/privacy/erase', { query: { email } }),
  },
  assistant: {
    status: () => request<AssistantStatus>('GET', '/v1/assistant/status'),
    ask: (message: string, history: Record<string, unknown>[]) =>
      request<AssistantTurn>('POST', '/v1/assistant/ask', {
        body: { message, history },
      }),
    confirm: (action: string, args: Record<string, unknown>) =>
      request<{ done: boolean }>('POST', '/v1/assistant/confirm', {
        body: { action, arguments: args },
      }),
  },
  billing: {
    get: () => request<BillingState>('GET', '/v1/billing'),
    plans: () =>
      request<{ current: string; plans: PlanSummary[] }>('GET', '/v1/billing/plans'),
    usage: (days = 30) =>
      request<UsageSeries>('GET', '/v1/billing/usage', { query: { days } }),
    checkout: (plan: string) =>
      request<{ url: string }>('POST', '/v1/billing/checkout', { query: { plan } }),
    portal: () => request<{ url: string }>('POST', '/v1/billing/portal'),
  },
};
